import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Organization-Admin dashboard API — calls endpoints exposed by
 * `OrganizationDashboardController` (@OrgAdminOnly), `OrganizationEmailDomainController`
 * (@OrgAdminOnly), and the org-owned course creation endpoint on `EndeavorController`.
 */
export const OrgAdminApi = {
  // ── Members ──────────────────────────────────────────────────────────────
  /** GET /api/organizations/{orgId}/admin/members */
  getMembers: async (organizationId) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/admin/members`, { method: 'GET' }),

  /**
   * PATCH /api/organizations/{orgId}/admin/members/{userId}/role
   * @param {string} orgRole one of 'org_admin' | 'course_admin' | 'org_member'
   */
  updateMemberRole: async (organizationId, userId, orgRole) =>
    fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/admin/members/${userId}/role`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgRole }),
      }
    ),

  /**
   * POST /api/organizations/{orgId}/admin/members — add user by email.
   * @param {string} email
   * @param {string} orgRole one of 'org_admin' | 'course_admin' | 'org_member'
   */
  addMember: async (organizationId, email, orgRole = 'org_member') =>
    fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/admin/members`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, orgRole }),
      }
    ),

  /** DELETE /api/organizations/{orgId}/admin/members/{userId} */
  removeMember: async (organizationId, userId) =>
    fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/admin/members/${userId}`,
      { method: 'DELETE' }
    ),

  // ── Courses ──────────────────────────────────────────────────────────────
  /** GET /api/organizations/{orgId}/admin/courses */
  getCourses: async (organizationId) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/admin/courses`, { method: 'GET' }),

  /** GET /api/organizations/{orgId}/admin/activity-feed (v1 stub) */
  getActivityFeed: async (organizationId) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/admin/activity-feed`, { method: 'GET' }),

  /** GET /api/organizations/{orgId}/admin/at-risk-students (v1 stub) */
  getAtRiskStudents: async (organizationId) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/admin/at-risk-students`, { method: 'GET' }),

  // ── Email Domains ────────────────────────────────────────────────────────
  /** GET /api/organizations/{orgId}/email-domains */
  getEmailDomains: async (organizationId) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/email-domains`, { method: 'GET' }),

  /**
   * POST /api/organizations/{orgId}/email-domains — upsert.
   * autoRole must be 'course_admin' or 'org_member' (org_admin rejected by backend).
   */
  upsertEmailDomain: async (organizationId, { domain, autoRole = 'org_member' }) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/email-domains`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain, autoRole }),
    }),

  /** DELETE /api/organizations/{orgId}/email-domains/{id} */
  deleteEmailDomain: async (organizationId, id) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/email-domains/${id}`, {
      method: 'DELETE',
    }),

  // ── Org-owned course creation ────────────────────────────────────────────
  /**
   * POST /api/organizations/{orgId}/courses
   * Backend gates with @OrgScoped(allowCourseAdmin=true).
   */
  createOrgOwnedCourse: async (organizationId, endeavor) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/organizations/${organizationId}/courses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(endeavor),
    }),
};
